import React, { useContext } from 'react';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Flex,
  Progress,
  Row,
  Tag,
  theme as antTheme,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import { ActivityItem } from '@totem/components/accountPlan/types';
import {
  ACTIVITY_COLORS,
  SectionHeading,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const COMPONENTS = [
  { key: 'operational', label: 'Operational' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'relationship', label: 'Relationship' },
] as const;

const HealthCard = () => {
  const { data, generating, generateNarrative } =
    useContext(AccountPlanContext);
  const { token } = antTheme.useToken();
  if (!data) {
    return null;
  }
  const { health, buildings } = data.summary;
  const { activity } = buildings;
  const { narrative } = data.plan;
  const narrativeStale = narrative && narrative.score !== health.score;

  const barColor = (score: number) => {
    if (score >= 75) {
      return token.colorSuccess;
    }
    if (score >= 60) {
      return token.colorWarning;
    }
    return token.colorError;
  };

  return (
    <PlanCard
      variant="live"
      title={`Account Health — how the ${health.score} is derived`}
      liveTip="Illustrative weighted model computed from live Portal data and the stakeholders / QBRs on this plan. Weights are set in Account setup."
      extra="Weighted model"
    >
      <Row gutter={24}>
        <Col xs={24} lg={12}>
          {COMPONENTS.map((component) => (
            <Flex key={component.key} align="center" gap={12}>
              <Typography.Text
                type="secondary"
                style={{ width: 130, flexShrink: 0 }}
              >
                {component.label} ({health.weights[component.key]}%)
              </Typography.Text>
              <Progress
                style={{ flex: 1, margin: 0 }}
                percent={health[component.key]}
                showInfo={false}
                strokeColor={barColor(health[component.key])}
              />
              <Typography.Text strong style={{ width: 28, textAlign: 'right' }}>
                {health[component.key]}
              </Typography.Text>
            </Flex>
          ))}
          <div style={{ marginTop: 10 }}>
            {narrative ? (
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, marginBottom: 4 }}
              >
                {narrative.text}
              </Typography.Paragraph>
            ) : (
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, marginBottom: 4 }}
              >
                No narrative yet.
              </Typography.Paragraph>
            )}
            <Flex align="center" gap={8} wrap>
              <Button
                size="small"
                loading={generating}
                onClick={generateNarrative}
              >
                {narrative ? 'Regenerate narrative' : 'Generate narrative'}
              </Button>
              <Tag>AI-assisted</Tag>
              {narrative && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(narrative.generatedAt).format('MMM D')} ·{' '}
                  {narrative.generatedBy}
                  {narrativeStale && ` · written for ${narrative.score}`}
                </Typography.Text>
              )}
            </Flex>
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <SectionHeading inCard>Auto-flagged risks</SectionHeading>
          {health.risks.length === 0 ? (
            <Typography.Text type="secondary">
              No risks flagged.
            </Typography.Text>
          ) : (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {health.risks.map((risk) => (
                <li key={risk}>{risk}</li>
              ))}
            </ul>
          )}
        </Col>
      </Row>

      <Collapse
        ghost
        size="small"
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'factors',
            label: (
              <Typography.Text type="secondary">
                Score factors ({health.factors.length})
              </Typography.Text>
            ),
            children: (
              <Row gutter={[16, 2]}>
                {health.factors.map((factor) => (
                  <Col
                    xs={24}
                    lg={12}
                    key={`${factor.component}-${factor.label}`}
                  >
                    <Flex justify="space-between">
                      <Typography.Text style={{ fontSize: 12 }}>
                        {factor.label}
                      </Typography.Text>
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 12,
                          color:
                            factor.points >= 0
                              ? token.colorSuccess
                              : token.colorError,
                        }}
                      >
                        {factor.points > 0
                          ? `+${factor.points}`
                          : factor.points}
                      </Typography.Text>
                    </Flex>
                  </Col>
                ))}
              </Row>
            ),
          },
        ]}
      />

      <Divider style={{ margin: '8px 0 12px' }} />
      <Flex align="center" gap={8} wrap style={{ marginBottom: 8 }}>
        <SectionHeading inCard>
          Portfolio activity this year ({activity.year})
        </SectionHeading>
        <Tag
          color={
            activity.trend === 'contracting'
              ? 'red'
              : activity.trend === 'expanding'
                ? 'green'
                : 'default'
          }
        >
          {activity.trend === 'expanding'
            ? 'Expanding'
            : activity.trend === 'contracting'
              ? 'Contracting'
              : 'Stable'}{' '}
          · {activity.added} buildings added · {activity.lost} lost
        </Tag>
      </Flex>
      <IBTable
        size="small"
        rowKey={(row: ActivityItem) => `${row.site}-${row.activity}`}
        columns={[
          { title: 'Site', dataIndex: 'site', width: 240 },
          { title: 'Activity', dataIndex: 'activity' },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 170,
            render: (status: string, row: ActivityItem) => (
              <Tag color={ACTIVITY_COLORS[row.kind]}>{status}</Tag>
            ),
          },
        ]}
        dataSource={activity.rows}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          hideOnSinglePage: true,
        }}
        locale={{ emptyText: 'No portfolio changes this year' }}
      />
    </PlanCard>
  );
};

export default HealthCard;
